/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import debounce from "lodash/debounce";

import {
  BaseBlockSnapshot,
  BlockType,
  CodeBlockSnapshot,
  ONE_SECOND,
  ProgrammingLanguage,
} from "@vienna/common/src";

import { PlateSocket } from "../../plate/PlateSocket/PlateSocket";
import { BaseBlock } from "src/classes/block/BaseBlock";
import { ClipboardUtils } from "src/classes/utilities/ClipboardUtils";

export class CodeBlock extends BaseBlock<CodeBlockSnapshot> {
  public static supportedLanguages = Object.values(ProgrammingLanguage).sort();

  constructor(snapshot: CodeBlockSnapshot, plateSocket: PlateSocket | null) {
    super(snapshot, plateSocket);
  }

  /* -------------------------------------------------- */
  /*                       State                        */
  /* -------------------------------------------------- */
  private _updatePlaintext = (plaintext: string): void => {
    if (!this.plateSocket) return;
    this.setState({ plainText: plaintext });
    this.plateSocket.saveBlockUpdate(this.snapshot, this.prevSnapshot);
  };

  public updatePlaintext = debounce(this._updatePlaintext, ONE_SECOND / 2, { maxWait: ONE_SECOND * 2 });

  public changeLanguage = (language: ProgrammingLanguage): void => {
    if (!this.plateSocket) return;
    this.setState({ language });
    this.plateSocket.saveBlockUpdate(this.snapshot, this.prevSnapshot);
  };

  public get codeEitorId(): string {
    //@todo this whole concept is dumb
    return `code-editor-${this.snapshot.id}`;
  }

  /* -------------------------------------------------- */
  /*                      Actions                      */
  /* -------------------------------------------------- */
  public copyText(): void {
    ClipboardUtils.copyTextToClipboard(this.snapshot.plainText);
  }

  /* -------------------------------------------------- */
  /*                   Static Methods                   */
  /* -------------------------------------------------- */
  // initialPlaintext is passed when converting from TextBlock (i.e. via slash menu)
  public static newSnapshot(baseSnapshot: BaseBlockSnapshot, initialPlaintext?: string): CodeBlockSnapshot {
    const snapshot: CodeBlockSnapshot = {
      ...baseSnapshot,
      type: BlockType.Code,
      language: ProgrammingLanguage.Javascript,
      plainText: initialPlaintext || "",
    };
    return snapshot;
  }
}
